import type { Opportunity, VocabularyEntry, VocabularyRef } from './opportunity';

/**
 * One city's everything: `public/data/<city>.json` (contract §1). Fetched at
 * runtime by `useCityData`, never bundled, and only the current city's file.
 */

export interface Neighborhood {
  key: string;
  label: string;
  borough: string;
  lat: number;
  lng: number;
}

/** The speeds the travel estimate assumes (Session 5). */
export interface TravelSpeeds {
  walkKmh: number;
  bikeKmh: number;
  transitKmh: number;
  transitOverheadMin: number;
  ferryKmh: number;
}

/** As the file holds it: roles and topics are bare keys. */
export type RawOpportunity = Omit<Opportunity, 'roles' | 'topics'> & { roles: string[]; topics: string[] };

interface CityFileBase {
  city: string;
  cityLabel: string;
  /** The word on the header pill. */
  cityShort: string;
  timezone: string;
  generatedAt: string;
  /** True when `scripts/demo/shift-dates` moved the dates forward: the list says so, marked Sample. */
  datesShifted: boolean;
  correctionsEmail: string;
  taxonomy: { roles: VocabularyEntry[]; topics: VocabularyEntry[] };
  neighborhoods: Neighborhood[];
  travel: TravelSpeeds;
}

export interface CityFileJson extends CityFileBase {
  opportunities: RawOpportunity[];
}

/** The file with every role and topic key turned into `{ key, label, icon }`, once, at load. */
export interface CityFile extends CityFileBase {
  opportunities: Opportunity[];
}

const bySortOrder = (a: VocabularyEntry, b: VocabularyEntry) => a.sortOrder - b.sortOrder;

export function hydrate(raw: CityFileJson): CityFile {
  const roles = [...raw.taxonomy.roles].sort(bySortOrder);
  const topics = [...raw.taxonomy.topics].sort(bySortOrder);
  const refs = (entries: VocabularyEntry[]) =>
    new Map<string, VocabularyRef>(entries.map((e) => [e.key, { key: e.key, label: e.label, icon: e.icon }]));
  const roleRefs = refs(roles);
  const topicRefs = refs(topics);
  // A key the taxonomy doesn't name is dropped rather than drawn as a bare key.
  const pick = (keys: string[] | undefined, table: Map<string, VocabularyRef>) =>
    (keys ?? []).map((k) => table.get(k)).filter((r): r is VocabularyRef => Boolean(r));

  return {
    ...raw,
    taxonomy: { roles, topics },
    opportunities: raw.opportunities.map((o) => ({
      ...o,
      roles: pick(o.roles, roleRefs),
      topics: pick(o.topics, topicRefs),
    })),
  };
}
